import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getEventos, getSessoesByEvento, getLocaisSessaoBySessao } from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Label } from '../ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card';
import { Calendar, Clock, MapPin } from 'lucide-react';

// Função auxiliar para truncar o nome
const truncateName = (name, maxLength = 30) => {
    if (!name) return '';
    if (name.length > maxLength) {
        return name.substring(0, maxLength) + '...';
    }
    return name;
};

function CheckinContextSelector({ 
    selectedEventId, 
    setSelectedEventId, 
    selectedSessionId, 
    setSelectedSessionId, 
    selectedLocalSessaoId, 
    setSelectedLocalSessaoId 
}) {
    const [events, setEvents] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [localSessoes, setLocalSessoes] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingLocals, setLoadingLocals] = useState(false);

    // 1. Fetch Events
    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true);
            try {
                const data = await getEventos();
                setEvents(data);
            } catch (error) {
                toast.error("Erro ao carregar eventos: " + error.message);
            } finally {
                setLoadingEvents(false);
            }
        };
        fetchEvents();
    }, []);

    // 2. Fetch Sessions when Event changes
    useEffect(() => {
        const fetchSessions = async () => {
            if (selectedEventId) {
                setLoadingSessions(true);
                try {
                    const data = await getSessoesByEvento(selectedEventId);
                    setSessions(data);
                    setSelectedSessionId('');
                    setLocalSessoes([]);
                    setSelectedLocalSessaoId('');
                } catch (error) {
                    toast.error("Erro ao carregar sessões: " + error.message);
                } finally {
                    setLoadingSessions(false);
                }
            } else {
                setSessions([]);
                setSelectedSessionId('');
                setLocalSessoes([]);
                setSelectedLocalSessaoId('');
            }
        };
        fetchSessions();
    }, [selectedEventId, setSelectedSessionId, setSelectedLocalSessaoId]);

    // 3. Fetch Local Sessoes when Session changes
    useEffect(() => {
        const fetchLocalSessoes = async () => {
            if (selectedSessionId) {
                setLoadingLocals(true);
                try {
                    const data = await getLocaisSessaoBySessao(selectedSessionId);
                    setLocalSessoes(data);
                    setSelectedLocalSessaoId('');
                } catch (error) {
                    toast.error("Erro ao carregar salas: " + error.message);
                } finally {
                    setLoadingLocals(false);
                }
            } else {
                setLocalSessoes([]);
                setSelectedLocalSessaoId('');
            }
        };
        fetchLocalSessoes();
    }, [selectedSessionId, setSelectedLocalSessaoId]);

    const currentEvent = events.find(e => String(e.id) === selectedEventId);
    const currentSession = sessions.find(s => String(s.id) === selectedSessionId);
    const currentLocal = localSessoes.find(l => String(l.id) === selectedLocalSessaoId);

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    };

    // Função para renderizar o valor selecionado truncado
    // Esta função agora é usada para renderizar o conteúdo do SelectValue
    const renderSelectValueContent = (item, placeholderText) => {
        if (item) {
            return truncateName(item.nome || item.nomeCompleto, 30);
        }
        return placeholderText;
    };

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle className="mb-2">Contexto de Check-in</CardTitle>
                <CardDescription className="leading-relaxed">
                    Selecione o evento, a sessão e a sala onde o check-in será realizado.
                </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
                {/* Evento Select */}
                <div className="grid gap-2">
                    <Label htmlFor="eventSelect" className="mb-1">1. Evento</Label>
                    <Select onValueChange={setSelectedEventId} value={selectedEventId} disabled={loadingEvents}>
                        <SelectTrigger id="eventSelect" className="overflow-hidden whitespace-nowrap text-ellipsis">
                            <SelectValue placeholder={loadingEvents ? "A carregar eventos..." : "Selecione um Evento"}>
                                {/* Renderiza o valor truncado se estiver selecionado */}
                                {selectedEventId && renderSelectValueContent(currentEvent, "")}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {events.map(event => (
                                <SelectItem key={event.id} value={String(event.id)}>{event.nome}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Sessão Select */}
                <div className="grid gap-2">
                    <Label htmlFor="sessionSelect" className="mb-1">2. Sessão</Label>
                    <Select onValueChange={setSelectedSessionId} value={selectedSessionId} disabled={!selectedEventId || loadingSessions}>
                        <SelectTrigger id="sessionSelect" className="overflow-hidden whitespace-nowrap text-ellipsis">
                            <SelectValue placeholder={loadingSessions ? "A carregar sessões..." : "Selecione uma Sessão"}>
                                {selectedSessionId && renderSelectValueContent(currentSession, "")}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {sessions.map(session => (
                                <SelectItem key={session.id} value={String(session.id)}>
                                    {session.nome}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Sala/Local Select */}
                <div className="grid gap-2">
                    <Label htmlFor="localSessaoSelect" className="mb-1">3. Sala/Local</Label>
                    <Select onValueChange={setSelectedLocalSessaoId} value={selectedLocalSessaoId} disabled={!selectedSessionId || loadingLocals || localSessoes.length === 0}>
                        <SelectTrigger id="localSessaoSelect" className="overflow-hidden whitespace-nowrap text-ellipsis">
                            <SelectValue placeholder={loadingLocals ? "A carregar salas..." : (localSessoes.length === 0 ? "Nenhuma sala disponível" : "Selecione uma Sala")}>
                                {selectedLocalSessaoId && renderSelectValueContent(currentLocal, "")}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {localSessoes.map(local => (
                                <SelectItem key={local.id} value={String(local.id)}>
                                    {local.nomeCompleto}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </CardContent>
            
            {/* Resumo do Contexto Ativo */}
            {(currentEvent || currentSession || currentLocal) && (
                <CardContent className="border-t pt-4 mt-4 bg-muted/30 rounded-b-lg">
                    <h4 className="text-sm font-semibold mb-2 text-primary">Contexto Ativo:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center">
                            <Calendar className="mr-2 h-4 w-4 text-primary/70" />
                            <span className="font-medium text-foreground">{currentEvent?.nome || 'Evento não selecionado'}</span>
                        </div>
                        <div className="flex items-center">
                            <Clock className="mr-2 h-4 w-4 text-primary/70" />
                            <span className="font-medium text-foreground">{currentSession?.nome || 'Sessão não selecionada'}</span>
                            {currentSession && <span className="ml-2 text-xs">({formatDateTime(currentSession.dataHoraInicio)})</span>}
                        </div>
                        <div className="flex items-center">
                            <MapPin className="mr-2 h-4 w-4 text-primary/70" />
                            <span className={`font-bold ${currentLocal ? 'text-green-600' : 'text-destructive'}`}>
                                {currentLocal?.nomeCompleto || 'Sala não selecionada'}
                            </span>
                        </div>
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

export default CheckinContextSelector;